import { lazy, Suspense, useEffect, useRef, useState, type RefObject } from 'react';
import { useLandmarkStore } from '../state/landmarks';
import { PhotoCarousel } from './PhotoCarousel';
import { LandmarkPanelTabs, type SidePanelTab } from './LandmarkPanelTabs';
import { LandmarkNotesTab } from './LandmarkNotesTab';
import { truncateToWords } from '../lib/truncate';
import { findSplatForLandmark, type SplatManifestEntry } from '../lib/splats';
import type { FeaturedLandmark } from '../types/landmark';

const SplatViewer = lazy(() =>
  import('./SplatViewer').then((m) => ({ default: m.SplatViewer })),
);
const BuildingContextViewer = lazy(() =>
  import('./BuildingContextViewer').then((m) => ({ default: m.BuildingContextViewer })),
);

const ANTWERP_3D_TILE_BOUNDS = { xMin: 152000, xMax: 153000, yMin: 212000, yMax: 213000 };

function isInAntwerp3DTile([lng, lat]: [number, number]): boolean {
  // Belgian Lambert 72 approximation for the 152_212 tile, calibrated for Antwerp center.
  // Roughly 4.395°-4.420°E, 51.214°-51.236°N — covers the historic centre incl. Het Steen,
  // Cathedral, Sint-Jacobskerk, Lange Nieuwstraat 81.
  if (lng < 4.388 || lng > 4.425) return false;
  if (lat < 51.213 || lat > 51.237) return false;
  void ANTWERP_3D_TILE_BOUNDS;
  return true;
}

const SUMMARY_WORD_LIMIT = 100;

export function LandmarkSidePanel() {
  const selectedLandmark = useLandmarkStore((state) => state.selectedLandmark);
  const selectLandmark = useLandmarkStore((state) => state.selectLandmark);
  const activeTab = useLandmarkStore((state) => state.activeTab);
  const setActiveTab = useLandmarkStore((state) => state.setActiveTab);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Move focus to the close button when a landmark is opened.
  useEffect(() => {
    if (selectedLandmark?.tier === 'featured') {
      closeButtonRef.current?.focus();
    }
  }, [selectedLandmark]);

  // ESC closes the panel.
  useEffect(() => {
    if (selectedLandmark?.tier !== 'featured') return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') selectLandmark(null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedLandmark, selectLandmark]);

  if (!selectedLandmark || selectedLandmark.tier !== 'featured') return null;
  return (
    <FeaturedPanelBody
      landmark={selectedLandmark}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      closeButtonRef={closeButtonRef}
      onClose={() => selectLandmark(null)}
    />
  );
}

interface FeaturedPanelBodyProps {
  landmark: FeaturedLandmark;
  activeTab: SidePanelTab;
  onTabChange: (tab: SidePanelTab) => void;
  closeButtonRef: RefObject<HTMLButtonElement>;
  onClose: () => void;
}

function FeaturedPanelBody({
  landmark,
  activeTab,
  onTabChange,
  closeButtonRef,
  onClose,
}: FeaturedPanelBodyProps) {
  return (
    <aside
      role="dialog"
      aria-modal="false"
      aria-labelledby="landmark-panel-title"
      data-testid="landmark-side-panel"
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        width: 'min(420px, 100vw)',
        background: 'var(--surface-raised)',
        boxShadow: 'var(--shadow-popover)',
        borderLeft: '1px solid var(--border)',
        padding: 'var(--space-6)',
        overflowY: 'auto',
        zIndex: 20,
      }}
    >
      <button
        ref={closeButtonRef}
        type="button"
        onClick={onClose}
        aria-label="Close landmark details"
        data-testid="landmark-panel-close"
        style={{
          position: 'absolute',
          top: 'var(--space-3)',
          right: 'var(--space-3)',
          width: 32,
          height: 32,
          borderRadius: 'var(--radius-sm)',
          color: 'var(--text-muted)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg
          width={18}
          height={18}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M6 6L18 18M18 6L6 18" />
        </svg>
      </button>

      <h2
        id="landmark-panel-title"
        style={{ marginTop: 'var(--space-4)', fontSize: '1.5rem', fontWeight: 600 }}
      >
        {landmark.name}
      </h2>

      {landmark.alternativeNames && landmark.alternativeNames.length > 0 ? (
        <p
          style={{
            marginTop: 'var(--space-1)',
            color: 'var(--text-muted)',
            fontStyle: 'italic',
            fontFamily: 'var(--font-serif)',
          }}
        >
          {landmark.alternativeNames.join(' · ')}
        </p>
      ) : null}

      {landmark.period ? (
        <p style={{ marginTop: 'var(--space-3)', color: 'var(--text-muted)' }}>{landmark.period}</p>
      ) : null}

      <LandmarkPanelTabs activeTab={activeTab} onChange={onTabChange} />

      {activeTab === 'about' ? (
        <AboutTab landmark={landmark} />
      ) : (
        <div id="tabpanel-notes" role="tabpanel">
          <LandmarkNotesTab landmarkId={landmark.id} />
        </div>
      )}
    </aside>
  );
}

function AboutTab({ landmark }: { landmark: FeaturedLandmark }) {
  const summaryText = landmark.wikipediaSummary ?? '';
  const { display: displaySummary, isTruncated } = summaryText.length > 0
    ? truncateToWords(summaryText, SUMMARY_WORD_LIMIT)
    : { display: '', isTruncated: false };

  const heritageRecordUrl = `https://inventaris.onroerenderfgoed.be/erfgoedobjecten/${landmark.oerId}`;

  const [splat, setSplat] = useState<SplatManifestEntry | null>(null);
  const [splatOpen, setSplatOpen] = useState(false);
  const [buildingContextOpen, setBuildingContextOpen] = useState(false);
  const hasBuildingContext = isInAntwerp3DTile(landmark.coordinates);

  useEffect(() => {
    let cancelled = false;
    setSplat(null);
    findSplatForLandmark(landmark)
      .then((entry) => {
        if (!cancelled) setSplat(entry);
      })
      .catch(() => {
        if (!cancelled) setSplat(null);
      });
    return () => {
      cancelled = true;
    };
  }, [landmark]);

  return (
    <div id="tabpanel-about" role="tabpanel" data-testid="tabpanel-about">
      {landmark.photos && landmark.photos.length > 0 ? (
        <PhotoCarousel photos={landmark.photos} />
      ) : null}

      {splat ? (
        <button
          type="button"
          onClick={() => setSplatOpen(true)}
          data-testid="open-splat-viewer"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            width: '100%',
            marginTop: 'var(--space-3)',
            padding: '10px 14px',
            background: 'var(--primary)',
            color: '#fffdf2',
            border: '1px solid var(--primary)',
            borderRadius: 'var(--radius-sm)',
            fontSize: '0.9rem',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <svg
            width={18}
            height={18}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M12 2 2 7l10 5 10-5-10-5z" />
            <path d="m2 17 10 5 10-5" />
            <path d="m2 12 10 5 10-5" />
          </svg>
          View in 3D
          <span
            style={{
              marginLeft: 'auto',
              fontSize: '0.7rem',
              fontFamily: 'var(--font-mono, ui-monospace, monospace)',
              opacity: 0.78,
            }}
          >
            {(splat.bytes / 1_048_576).toFixed(0)} MB
          </span>
        </button>
      ) : null}

      {splat && splatOpen ? (
        <Suspense fallback={null}>
          <SplatViewer
            splat={splat}
            landmarkName={landmark.name}
            onClose={() => setSplatOpen(false)}
          />
        </Suspense>
      ) : null}

      {hasBuildingContext ? (
        <button
          type="button"
          onClick={() => setBuildingContextOpen(true)}
          data-testid="open-building-context"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            width: '100%',
            marginTop: 'var(--space-2)',
            padding: '10px 14px',
            background: 'var(--surface)',
            color: 'var(--text)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            fontSize: '0.9rem',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <svg
            width={18}
            height={18}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M3 21V7l9-4 9 4v14" />
            <path d="M9 21V12h6v9" />
          </svg>
          View building context
          <span
            style={{
              marginLeft: 'auto',
              fontSize: '0.7rem',
              fontFamily: 'var(--font-mono, ui-monospace, monospace)',
              color: 'var(--text-muted)',
            }}
          >
            27 MB
          </span>
        </button>
      ) : null}

      {buildingContextOpen ? (
        <Suspense fallback={null}>
          <BuildingContextViewer
            landmarkName={landmark.name}
            coordinates={landmark.coordinates}
            onClose={() => setBuildingContextOpen(false)}
          />
        </Suspense>
      ) : null}

      {displaySummary.length > 0 ? (
        <>
          <p
            data-testid="landmark-summary"
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: '1rem',
              lineHeight: 1.55,
            }}
          >
            {displaySummary}
            {landmark.wikipediaUrl ? (
              <>
                {' '}
                <a
                  href={landmark.wikipediaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="wikipedia-link"
                >
                  {isTruncated ? 'Read more →' : 'View on Wikipedia →'}
                </a>
              </>
            ) : null}
          </p>
          <p style={{ marginTop: 'var(--space-2)', color: 'var(--text-muted)', fontSize: '0.75rem' }}>
            From{' '}
            <a href="https://en.wikipedia.org/" target="_blank" rel="noopener noreferrer">
              Wikipedia
            </a>{' '}
            ·{' '}
            <a
              href="https://creativecommons.org/licenses/by-sa/4.0/"
              target="_blank"
              rel="noopener noreferrer license"
            >
              CC-BY-SA 4.0
            </a>
          </p>
        </>
      ) : null}

      <p style={{ marginTop: 'var(--space-6)', fontSize: '0.875rem' }}>
        <a
          href={heritageRecordUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="heritage-record-link"
        >
          View official heritage record →
        </a>
      </p>
    </div>
  );
}
